'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnOrderState, OnChangeFn, Updater } from '@tanstack/react-table';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';

export type TableColumnOrder = string[];
export type TableOnColumnReorder = (state: TableColumnOrder) => void;

function getInitialColumnOrder(controlledOrder?: TableColumnOrder) {
  if (controlledOrder) {
    return controlledOrder;
  }

  return STABLE_EMPTY_ARRAY_REF;
}

export function useColumnOrder(
  controlledColumnOrder?: TableColumnOrder,
  onColumnReorder?: TableOnColumnReorder,
): [ColumnOrderState, OnChangeFn<ColumnOrderState>] {
  const hasUserUpdatedRef = useRef(false);
  const [state, setState] = useState<ColumnOrderState>(getInitialColumnOrder(controlledColumnOrder));
  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback(
    (updater: Updater<ColumnOrderState>) => {
      const result = typeof updater === 'function' ? updater(state) : updater;
      setState(result);

      hasUserUpdatedRef.current = true;

      if (onColumnReorder) {
        onColumnReorder(result as TableColumnOrder);
      }

      return result;
    },
    [state, onColumnReorder],
  );

  useEffect(() => {
    if (!hasUserUpdatedRef.current) {
      setState(getInitialColumnOrder(controlledColumnOrder));
    }
  }, [controlledColumnOrder]);

  return useMemo(() => [state, handleColumnOrderChange], [state, handleColumnOrderChange]);
}
